import { create } from "zustand";
import { persist } from "zustand/middleware";

export const SSO_DASHBOARD_URL = "https://auth.growthpal.com";
export const GROWTHPAL_ACCESS_TOKEN = "growthpal_token";


const REFRESH_MESSAGE_TYPE = "SSO_REFRESH_RESULT";
const REFRESH_TIMEOUT_MS = 15_000;

let bridgeState = null;
let listenerAttached = false;
let iframeRef = null;

const getSsoOrigin = () => {
  try { return new URL(SSO_DASHBOARD_URL).origin; }
  catch { return SSO_DASHBOARD_URL; }
};

const createRequestId = () =>
  typeof crypto !== "undefined" && "randomUUID" in crypto
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(16).slice(2)}`;

const buildRefreshUrl = (requestId) => {
  const url = new URL("/refresh", SSO_DASHBOARD_URL);
  url.searchParams.set("redirectTo", window.location.href);
  url.searchParams.set("origin", window.location.origin);
  url.searchParams.set("requestId", requestId);
  url.searchParams.set("ts", Date.now().toString());
  return url.toString();
};

const ensureIframe = () => {
  if (iframeRef?.isConnected) {
    iframeRef.src = "about:blank";
    return iframeRef;
  }
  const iframe = document.createElement("iframe");
  iframe.style.cssText = "display:none;width:0;height:0;border:0";
  iframe.setAttribute("aria-hidden", "true");
  iframe.tabIndex = -1;
  (document.body ?? document.documentElement).appendChild(iframe);
  iframeRef = iframe;
  return iframe;
};

const cleanupBridge = () => {
  bridgeState = null;
  listenerAttached = false;
  window.removeEventListener("message", onMessage);
  if (iframeRef) iframeRef.src = "about:blank";
};

const onMessage = (event) => {
  if (!bridgeState) return;
  if (event.origin !== getSsoOrigin()) return;
  const data = event.data;
  if (!data || data.type !== REFRESH_MESSAGE_TYPE) return;
  if (data.requestId && data.requestId !== bridgeState.requestId) return;

  if (data.success && data.accessToken) {
    bridgeState.resolve(data.accessToken);
  } else {
    bridgeState.reject(new Error(data.error || "SSO refresh failed"));
  }
};

const ensureListener = () => {
  if (listenerAttached || typeof window === "undefined") return;
  listenerAttached = true;
  window.addEventListener("message", onMessage);
};

export const requestSsoAccessToken = () => {
  if (typeof window === "undefined" || typeof document === "undefined") {
    return Promise.reject(new Error("Token refresh is only available in the browser"));
  }

  if (bridgeState?.promise) return bridgeState.promise;

  const requestId = createRequestId();
  let resolvePromise, rejectPromise;

  const promise = new Promise((resolve, reject) => {
    resolvePromise = resolve;
    rejectPromise = reject;
  });

  const timeoutId = window.setTimeout(() => {
    bridgeState?.reject(new Error("SSO refresh timed out"));
  }, REFRESH_TIMEOUT_MS);

  bridgeState = {
    requestId,
    timeoutId,
    promise,
    resolve: (token) => {
      window.clearTimeout(timeoutId);
      cleanupBridge();
      resolvePromise(token);
    },
    reject: (err) => {
      window.clearTimeout(timeoutId);
      cleanupBridge();
      rejectPromise(err);
    },
  };

  ensureListener();
  ensureIframe().src = buildRefreshUrl(requestId);
  return promise;
};


let initAuthPromise = null;

export const useAuthStore = create(
  persist(
    (set, get) => ({
      user: null,
      token: null,
      growthpalToken: null,
      isAuthenticated: false,
      isAuthReady: false,

      setGrowthPalToken: (token) => {
        localStorage.setItem(GROWTHPAL_ACCESS_TOKEN, token);
        set({ growthpalToken: token });
      },

      getGrowthPalToken: () => {
        return localStorage.getItem(GROWTHPAL_ACCESS_TOKEN);
      },

      initAuth: async () => {
        if (initAuthPromise) return initAuthPromise;

        initAuthPromise = (async () => {
          try {
            const urlParams = new URLSearchParams(window.location.search);
            const urlToken = urlParams.get("accessToken");
            if (urlToken) {
              localStorage.setItem(GROWTHPAL_ACCESS_TOKEN, urlToken);
              window.history.replaceState({}, document.title, window.location.pathname);
            }

            let token = localStorage.getItem(GROWTHPAL_ACCESS_TOKEN);

            if (!token) {
              try {
                token = await requestSsoAccessToken();
                localStorage.setItem(GROWTHPAL_ACCESS_TOKEN, token);
              } catch {
                  window.location.href = `${SSO_DASHBOARD_URL}?redirectTo=${encodeURIComponent(window.location.href)}`;
                return false;
              }
            }

            set({ growthpalToken: token, isAuthenticated: true });

            const success = await get().loginToBackend();
            set({ isAuthReady: true });
            return success;
          } finally {
            initAuthPromise = null;
          }
        })();

        return initAuthPromise;
      },

      loginToBackend: async () => {
        try {
          const response = await fetch("/api/auth/login", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              email: "demo@growthpal.com",
              password: "demo",
            }),
          });

          if (!response.ok) throw new Error("Backend login failed");

          const data = await response.json();
          set({
            user: data.user,
            token: data.token,
            isAuthenticated: true,
          });

          return true;
        } catch {
          return false;
        }
      },

      logout: () => {
        localStorage.removeItem(GROWTHPAL_ACCESS_TOKEN);
        cleanupBridge(); 
        set({
          user: null,
          token: null,
          growthpalToken: null,
          isAuthenticated: false,
          isAuthReady: false,
        });
      },
    }),
    {
      name: "auth-storage",
      partialize: (state) => ({
        user: state.user,
        token: state.token,
        growthpalToken: state.growthpalToken,
        isAuthenticated: state.isAuthenticated,
      }),
    },
  ),
);